import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessages } from 'dignity.js/react';
import { LOBBY_SCOPE } from './node.js';
import { rememberPeerProfiles } from './playerProfiles.js';
import {
  loadCachedLobbyDirectory,
  saveCachedLobbyDirectory
} from './lobbyDirectoryCache.js';
import { fetchLobbyRendezvousPeers } from './lobbyRendezvous.js';
import { lobbyDebug } from './lobbyDebug.js';
import {
  LOBBY_DIRECTORY_BROADCAST_MS,
  LOBBY_DIRECTORY_MESSAGE_TYPE,
  LOBBY_MESH_FANOUT,
  LOBBY_MESH_INTERVAL_MS,
  LOBBY_PRESENCE_WARMUP_MS,
  LOBBY_RECENT_ACTIVE_LIMIT,
  LOBBY_RELINK_INTERVAL_MS,
  LOBBY_RENDEZVOUS_INTERVAL_MS,
  buildLobbyDirectoryPayload,
  lobbyPeersFromDirectoryPayload,
  lobbyPeersFromHellos,
  lobbyPeersFromRoom,
  lobbyRegistryFingerprint,
  recentPeersAsRoomPeers,
  selectFreshDialPeerIds,
  selectRandomMeshTargets,
  upsertRecentLobbyPeers
} from './lobbyMesh.js';

const PRESENCE_POLL_MS = 1_000;

export default function useLobbyMesh({
  node,
  joined,
  identity,
  nickname,
  country,
  openToChallenges,
  archivist = false,
  certShard = '',
  remotePeers = [],
  helloMessages = [],
  bootstrapPeer = '',
  openPeerIds = [],
  onIdentityPulse,
  onBasketChallenges,
  onBasketChats,
  onBasketRooms,
  getChatSyncPayload = null
}) {
  const [recentPeers, setRecentPeers] = useState(() => (
    loadCachedLobbyDirectory({
      selfPeerId: identity.peerId,
      selfPlayerId: identity.playerId
    })
  ));
  const recentRef = useRef(recentPeers);
  const remotePeersRef = useRef(remotePeers);
  const helloMessagesRef = useRef(helloMessages);
  const onIdentityPulseRef = useRef(onIdentityPulse);
  const onBasketChallengesRef = useRef(onBasketChallenges);
  const onBasketChatsRef = useRef(onBasketChats);
  const onBasketRoomsRef = useRef(onBasketRooms);
  const getChatSyncPayloadRef = useRef(getChatSyncPayload);
  const identityPostedRef = useRef(false);
  remotePeersRef.current = remotePeers;
  helloMessagesRef.current = helloMessages;
  onIdentityPulseRef.current = onIdentityPulse;
  onBasketChallengesRef.current = onBasketChallenges;
  onBasketChatsRef.current = onBasketChats;
  onBasketRoomsRef.current = onBasketRooms;
  getChatSyncPayloadRef.current = getChatSyncPayload;

  const directoryFilter = useCallback(
    (message) => message.type === LOBBY_DIRECTORY_MESSAGE_TYPE,
    []
  );
  const directoryMessages = useMessages(node, directoryFilter);

  const ingest = useCallback((entries) => {
    const next = upsertRecentLobbyPeers(recentRef.current, entries, {
      excludePeerIds: [identity.peerId],
      excludePlayerIds: [identity.playerId],
      limit: LOBBY_RECENT_ACTIVE_LIMIT
    });
    const identityChanged = (
      lobbyRegistryFingerprint(next) !== lobbyRegistryFingerprint(recentRef.current)
    );
    // Keep lastSeenAt fresh in the ref for mesh ranking even when UI identity is unchanged.
    recentRef.current = next;
    if (!identityChanged) {
      return false;
    }
    rememberPeerProfiles(next);
    saveCachedLobbyDirectory(next);
    setRecentPeers(next);
    return true;
  }, [identity.peerId, identity.playerId]);

  const remotePeersKey = remotePeers
    .map((peer) => [
      peer.peerId,
      peer.metadata?.nickname || '',
      peer.metadata?.country || '',
      peer.metadata?.playerId || '',
      peer.metadata?.openToChallenges === false ? '0' : '1',
      peer.metadata?.archivist ? '1' : '0'
    ].join(':'))
    .sort()
    .join('|');
  // Latest identity snapshot per sender — ignore per-hello sentAt churn.
  const helloMessagesKey = (() => {
    const latest = new Map();
    for (const message of helloMessages) {
      if (!message?.senderId) {
        continue;
      }
      latest.set(message.senderId, [
        message.senderId,
        message.payload?.nickname || '',
        message.payload?.country || '',
        message.payload?.playerId || '',
        message.payload?.openToChallenges === false ? '0' : '1',
        message.payload?.archivist ? '1' : '0'
      ].join(':'));
    }
    return [...latest.values()].sort().join('|');
  })();
  // Include rooms + recentChat — peer identity alone is stable while chat moves.
  const directoryKey = directoryMessages
    .map((message) => [
      message.senderId,
      (message.payload?.peers || [])
        .map((peer) => `${peer.peerId}:${peer.nickname || ''}:${peer.country || ''}`)
        .join(','),
      (message.payload?.rooms || [])
        .map((room) => `${room.roomId}:${room.createdAt || 0}`)
        .join(','),
      (message.payload?.recentChat || [])
        .map((entry) => `${entry.playerId}:${entry.sentAt}:${String(entry.text || '').slice(0, 24)}`)
        .join(',')
    ].join(':'))
    .join('|');
  const openPeersKey = [...openPeerIds].filter(Boolean).sort().join('|');

  useEffect(() => {
    ingest(lobbyPeersFromRoom(remotePeersRef.current));
  }, [ingest, remotePeersKey]);

  useEffect(() => {
    ingest(lobbyPeersFromHellos(helloMessagesRef.current));
  }, [helloMessagesKey, ingest]);

  useEffect(() => {
    if (!directoryMessages.length) {
      return;
    }
    const latest = directoryMessages[directoryMessages.length - 1];
    const payload = latest.payload || {};
    ingest(lobbyPeersFromDirectoryPayload(payload));
    if (typeof onBasketChatsRef.current === 'function' && Array.isArray(payload.recentChat)) {
      onBasketChatsRef.current(payload.recentChat.map((entry) => ({
        ...entry,
        senderId: entry.senderId || entry.playerId,
        messageKey: `${entry.senderId || entry.playerId}:${entry.sentAt}:${String(entry.text || '').slice(0, 24)}`,
        via: 'directory'
      })));
    }
    if (typeof onBasketRoomsRef.current === 'function' && Array.isArray(payload.rooms)) {
      onBasketRoomsRef.current(payload.rooms);
    }
  }, [directoryKey, directoryMessages, ingest]);

  // dignity.js usePeers only refreshes on first discovery; poll so later
  // presence metadata (nickname/country) still reaches Online now.
  useEffect(() => {
    if (!node || !joined || typeof node.listPeers !== 'function') {
      return undefined;
    }
    const syncPresence = () => {
      try {
        ingest(lobbyPeersFromRoom(
          node.listPeers(LOBBY_SCOPE, { includeSelf: false }) || []
        ));
      } catch {
        // Presence sync is best-effort.
      }
    };
    syncPresence();
    const timer = window.setInterval(syncPresence, PRESENCE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [ingest, joined, node]);

  const connectTargets = useCallback(async (peerIds) => {
    if (!node || !peerIds.length || typeof node.ensureConnectedToPeers !== 'function') {
      return [];
    }
    const unique = [...new Set(peerIds.filter(Boolean))]
      .filter((peerId) => peerId !== identity.peerId);
    if (!unique.length) {
      return [];
    }
    try {
      // Half-mesh first: lower peerId dials higher to avoid simultaneous PeerJS glare.
      const initiate = unique.filter((peerId) => identity.peerId < peerId);
      const acceptSide = unique.filter((peerId) => identity.peerId > peerId);
      const results = [];
      if (initiate.length) {
        const batch = await node.ensureConnectedToPeers(initiate);
        results.push(...(Array.isArray(batch) ? batch : []));
      }
      if (acceptSide.length) {
        // Brief pause so the peer that should initiate can open first.
        await new Promise((resolve) => window.setTimeout(resolve, 700));
        const batch = await node.ensureConnectedToPeers(acceptSide);
        results.push(...(Array.isArray(batch) ? batch : []));
      }
      const open = new Set(node.getConnectionStats?.().peerIds || []);
      const linked = unique.filter((peerId) => open.has(peerId));
      const failed = results
        .filter((entry) => entry && entry.ok === false)
        .map((entry) => ({
          peerId: entry.peerId,
          error: entry.error?.message || String(entry.error || 'failed')
        }));
      lobbyDebug('mesh-connect', {
        attempted: unique.length,
        initiate: initiate.length,
        acceptSide: acceptSide.length,
        linked: linked.length,
        openPeerIds: [...open],
        failed: failed.slice(0, 6)
      });
      return linked;
    } catch (error) {
      lobbyDebug('mesh-connect-error', {
        message: error?.message || String(error),
        attempted: unique.length
      });
      return [];
    }
  }, [identity.peerId, node]);

  const broadcastDirectory = useCallback(async () => {
    if (!node || !joined) {
      return;
    }
    const chatSync = typeof getChatSyncPayloadRef.current === 'function'
      ? getChatSyncPayloadRef.current()
      : { rooms: [], recentChat: [] };
    const payload = buildLobbyDirectoryPayload(recentRef.current, {
      selfPeerId: identity.peerId,
      selfPlayerId: identity.playerId,
      nickname,
      country,
      openToChallenges,
      archivist,
      certShard,
      rooms: chatSync.rooms || [],
      recentChat: chatSync.recentChat || []
    });
    const connectToPeers = (openPeerIds || []).filter(
      (peerId) => peerId && peerId !== identity.peerId
    );
    try {
      await node.broadcastMessage(LOBBY_DIRECTORY_MESSAGE_TYPE, payload, {
        broadcastScope: LOBBY_SCOPE,
        connectToPeers
      });
    } catch {
      // Ignore transient broadcast failures.
    }
  }, [
    archivist,
    certShard,
    country,
    identity.peerId,
    identity.playerId,
    joined,
    nickname,
    node,
    openPeerIds,
    openToChallenges
  ]);

  const refreshMesh = useCallback(async () => {
    if (!node || !joined) {
      return [];
    }
    const targets = selectRandomMeshTargets(recentRef.current, {
      count: LOBBY_MESH_FANOUT,
      excludePeerIds: [identity.peerId],
      preferPeerIds: [
        ...(bootstrapPeer ? [bootstrapPeer] : []),
        ...openPeerIds
      ]
    });
    const linked = await connectTargets(targets);
    if (linked.length && typeof onIdentityPulseRef.current === 'function') {
      onIdentityPulseRef.current(linked);
    }
    return linked;
  }, [bootstrapPeer, connectTargets, identity.peerId, joined, node, openPeerIds]);

  const refreshRendezvous = useCallback(async ({ force = false, connect = true } = {}) => {
    if (!node || !joined) {
      return { peers: recentRef.current, challenges: [], linked: [] };
    }
    try {
      const includeIdentity = !identityPostedRef.current;
      const snapshot = await fetchLobbyRendezvousPeers(identity.peerId, {
        scope: LOBBY_SCOPE,
        nickname,
        country,
        playerId: identity.playerId,
        force,
        includeIdentity
      });
      if (includeIdentity) {
        identityPostedRef.current = true;
      }
      const entries = snapshot?.peers || [];
      const challenges = snapshot?.challenges || [];
      const chats = snapshot?.chats || [];
      const rooms = snapshot?.rooms || [];
      if (entries.length) {
        ingest(entries);
      }
      if (
        challenges.length
        && typeof onBasketChallengesRef.current === 'function'
      ) {
        lobbyDebug('basket-challenges-ingest', {
          count: challenges.length,
          ids: challenges.map((entry) => entry.challengeId || entry.gameId)
        });
        onBasketChallengesRef.current(challenges);
      }
      if (
        chats.length
        && typeof onBasketChatsRef.current === 'function'
      ) {
        lobbyDebug('basket-chats-ingest', {
          count: chats.length,
          texts: chats.map((entry) => entry.text)
        });
        onBasketChatsRef.current(chats);
      }
      if (
        rooms.length
        && typeof onBasketRoomsRef.current === 'function'
      ) {
        onBasketRoomsRef.current(rooms);
      }
      // Prefer one fresh PeerJS session per player — ghosts break dials.
      const meshSource = entries.length ? entries : recentRef.current;
      let linked = [];
      if (connect && meshSource.length) {
        const targets = selectFreshDialPeerIds(meshSource, {
          selfPeerId: identity.peerId,
          limit: LOBBY_MESH_FANOUT,
          maxAgeMs: 0
        });
        linked = await connectTargets(targets);
        lobbyDebug('mesh-dial', {
          source: entries.length ? 'basket' : 'cache',
          targets: targets.length,
          linked: linked.length,
          targetIds: targets
        });
        if (linked.length && typeof onIdentityPulseRef.current === 'function') {
          onIdentityPulseRef.current(linked);
        }
      }
      return {
        peers: entries.length ? entries : recentRef.current,
        challenges,
        chats,
        rooms,
        linked
      };
    } catch {
      // Public rendezvous is optional; invite links still work.
      return {
        peers: recentRef.current,
        challenges: [],
        chats: [],
        rooms: [],
        linked: []
      };
    }
  }, [
    bootstrapPeer,
    connectTargets,
    country,
    identity.peerId,
    identity.playerId,
    ingest,
    joined,
    nickname,
    node,
    openPeerIds
  ]);

  const ensureLinkedToPeerIds = useCallback(async (peerIds = []) => {
    const targets = [...new Set((peerIds || []).filter(Boolean))];
    if (!targets.length) {
      return [];
    }
    return connectTargets(targets);
  }, [connectTargets]);

  const refreshRendezvousRef = useRef(refreshRendezvous);
  const refreshMeshRef = useRef(refreshMesh);
  const broadcastDirectoryRef = useRef(broadcastDirectory);
  refreshRendezvousRef.current = refreshRendezvous;
  refreshMeshRef.current = refreshMesh;
  broadcastDirectoryRef.current = broadcastDirectory;

  // Stable timers: do not restart on nickname/directory callback churn (that caused 429s).
  useEffect(() => {
    if (!node || !joined) {
      return undefined;
    }

    // Initial force poll; steady-state soft polls stay inside PeerBasket's ~20/min IP budget.
    void refreshRendezvousRef.current({ force: true, connect: true });
    void refreshMeshRef.current();
    void broadcastDirectoryRef.current();

    const warmupTimer = window.setTimeout(() => {
      void refreshRendezvousRef.current({ force: true, connect: true });
      void broadcastDirectoryRef.current();
    }, LOBBY_PRESENCE_WARMUP_MS);
    const rendezvousTimer = window.setInterval(() => {
      void refreshRendezvousRef.current({ force: false, connect: true });
    }, LOBBY_RENDEZVOUS_INTERVAL_MS);
    const meshTimer = window.setInterval(() => {
      void refreshMeshRef.current();
    }, LOBBY_MESH_INTERVAL_MS);
    const directoryTimer = window.setInterval(() => {
      void broadcastDirectoryRef.current();
    }, LOBBY_DIRECTORY_BROADCAST_MS);

    return () => {
      window.clearTimeout(warmupTimer);
      window.clearInterval(rendezvousTimer);
      window.clearInterval(meshTimer);
      window.clearInterval(directoryTimer);
    };
  }, [joined, node]);

  // Hard-refresh recovery: cached Online-now peers are visible without links.
  // Re-join discovery with their peer IDs and force PeerJS reconnects for chat.
  useEffect(() => {
    if (!node || !joined) {
      return undefined;
    }
    let cancelled = false;
    let inflight = null;
    const relink = async () => {
      if (inflight) {
        return;
      }
      inflight = (async () => {
        // One live PeerJS session per player — stale hard-refresh ids destroy dials.
        let ids = selectFreshDialPeerIds(recentRef.current, {
          selfPeerId: identity.peerId,
          limit: Math.min(8, LOBBY_MESH_FANOUT),
          maxAgeMs: 45_000
        });
        if (!ids.length) {
          ids = selectFreshDialPeerIds(recentRef.current, {
            selfPeerId: identity.peerId,
            limit: Math.min(8, LOBBY_MESH_FANOUT),
            maxAgeMs: 0
          });
        }
        if (!ids.length) {
          return;
        }
        if (typeof node.joinDiscovery === 'function') {
          await node.joinDiscovery(LOBBY_SCOPE, {
            bootstrapPeerIds: ids
          }).catch(() => undefined);
        }
        if (cancelled) {
          return;
        }
        const linked = await connectTargets(ids);
        lobbyDebug('mesh-relink', {
          targets: ids.length,
          linked: linked.length,
          freshOnly: true,
          targetIds: ids
        });
        if (!cancelled && linked.length && typeof onIdentityPulseRef.current === 'function') {
          onIdentityPulseRef.current(linked);
        }
        if (!cancelled && linked.length) {
          void broadcastDirectoryRef.current();
        }
      })();
      try {
        await inflight;
      } finally {
        inflight = null;
      }
    };
    void relink();
    // First relink is immediate. Later passes stay sparse: new dials are also
    // capped in the PeerJS adapter so directory sweeps cannot flood signaling.
    const timer = window.setInterval(() => {
      void relink();
    }, LOBBY_RELINK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [connectTargets, identity.peerId, joined, node, recentPeers.length]);

  // When a direct link opens, push directory so the peer learns our nickname/flag.
  useEffect(() => {
    if (!node || !joined || !openPeersKey) {
      return;
    }
    void broadcastDirectoryRef.current();
    if (typeof onIdentityPulseRef.current === 'function') {
      onIdentityPulseRef.current(openPeersKey.split('|').filter(Boolean));
    }
  }, [joined, node, openPeersKey]);

  const onlinePeers = useMemo(
    () => recentPeersAsRoomPeers(recentPeers),
    [recentPeers]
  );

  return {
    recentPeers,
    onlinePeers,
    refreshRendezvous,
    ensureLinkedToPeerIds,
    broadcastDirectory
  };
}
